import { SeededRandom } from "../core/SeededRandom";
import { PALETTE } from "./palettes";

export type ProceduralTextureKind =
  | "rustedRivets"
  | "grimyStoneBlocks"
  | "ventMetal"
  | "industrialWall"
  | "damagedWall"
  | "metalPanels"
  | "computerPanel"
  | "warningStripe"
  | "floorPlate"
  | "ceilingPanel"
  | "overgrownCeilingTile"
  | "toxicFloor"
  | "door"
  | "exitDoor"
  | "crate"
  | "bloodDecal"
  | "muzzleFlash"
  | "projectile"
  | "particle"
  | "medievalBrickWall"
  | "medievalBrickDamaged"
  | "sootBrickWall"
  | "dampBrickWall"
  | "overgrownBrickWall"
  | "brickTechJunction"
  | "brickArchTrim"
  | "darkStoneFloor"
  | "cableTrenchFloor"
  | "rivetedBlastDoor"
  | "amberTerminal"
  | "greenEnergyChannel"
  | "ritualReactorCasing"
  | "organicCorruption"
  | "bloodSmear"
  | "ashDecal"
  | "hazardPlateMuted";

const TEXTURE_SIZE = 128;

export class TextureGenerator {
  private readonly cache = new Map<ProceduralTextureKind, HTMLCanvasElement>();

  public constructor(private readonly seed: string) {}

  public get(kind: ProceduralTextureKind): HTMLCanvasElement {
    const cached = this.cache.get(kind);
    if (cached) {
      return cached;
    }
    const canvas = document.createElement("canvas");
    canvas.width = TEXTURE_SIZE * 2;
    canvas.height = TEXTURE_SIZE * 2;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas 2D context is unavailable");
    }
    context.imageSmoothingEnabled = false;
    context.scale(2, 2);
    const random = new SeededRandom(`${this.seed}:texture:${kind}`);
    this.drawBase(context, random, kind);
    this.cache.set(kind, canvas);
    return canvas;
  }

  private drawBase(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    kind: ProceduralTextureKind,
  ): void {
    switch (kind) {
      case "rustedRivets":
        this.drawPanels(context,random,"#302d24","#68644b",64);
        this.drawScratches(context,random,60,"rgba(146,73,30,.5)");
        this.drawRivets(context);
        break;
      case "grimyStoneBlocks":
        this.drawBricks(context,random,"#1d211a","#5e6150","#8b8970",64,32);
        this.drawSoot(context,random,0.18);
        this.drawVegetationPatches(context,random,"wall");
        break;
      case "ventMetal":
        this.drawPanels(context,random,"#343c32","#777965",64);
        context.fillStyle="#090e0b";
        context.beginPath();context.arc(64,64,52,0,Math.PI*2);context.fill();
        context.strokeStyle="#93967a";context.lineWidth=3;context.stroke();
        this.drawRivets(context);
        break;
      case "industrialWall":
        this.drawPanels(context, random, PALETTE.iron, PALETTE.steel, 32);
        break;
      case "damagedWall":
        this.drawPanels(context, random, PALETTE.darkRust, PALETTE.iron, 32);
        this.drawScratches(context, random, 28, PALETTE.black);
        break;
      case "metalPanels":
        this.drawPanels(context, random, "#303934", "#5b655d", 16);
        break;
      case "computerPanel":
        this.drawComputerPanel(context, random);
        break;
      case "warningStripe":
        this.drawWarningStripes(context);
        break;
      case "floorPlate":
        this.drawFloor(context, random, PALETTE.iron);
        break;
      case "ceilingPanel":
        this.drawPanels(context, random, "#222723", "#343c35", 32);
        break;
      case "overgrownCeilingTile":
        this.drawCeilingTiles(context, random);
        this.drawVegetationPatches(context, random, "ceiling");
        break;
      case "toxicFloor":
        this.drawToxic(context, random);
        break;
      case "door":
        this.drawDoor(context, false);
        break;
      case "exitDoor":
        this.drawDoor(context, true);
        break;
      case "crate":
        this.drawCrate(context, random);
        break;
      case "bloodDecal":
        this.drawSplatter(context, random, PALETTE.red);
        break;
      case "muzzleFlash":
        this.drawBurst(context, PALETTE.amber);
        break;
      case "projectile":
        this.drawBurst(context, "#ff6338");
        break;
      case "particle":
        this.drawSplatter(context, random, PALETTE.amber);
        break;
      case "medievalBrickWall":
        this.drawBricks(context, random, "#201713", "#594031", "#79604b");
        break;
      case "medievalBrickDamaged":
        this.drawBricks(context, random, "#1c1411", "#543326", "#76503a");
        this.drawScratches(context, random, 34, "rgba(5,3,2,.72)");
        break;
      case "sootBrickWall":
        this.drawBricks(context, random, "#100e0d", "#302621", "#4e3b31");
        this.drawSoot(context, random);
        break;
      case "dampBrickWall":
        this.drawBricks(context, random, "#111614", "#3f5046", "#64715b");
        this.drawVegetationPatches(context, random, "wall");
        break;
      case "overgrownBrickWall":
        this.drawBricks(context, random, "#181f18", "#4c5344", "#777969");
        this.drawVegetationPatches(context, random, "wall");
        break;
      case "brickTechJunction":
        this.drawBricks(context, random, "#17120f", "#493126", "#6b4938");
        this.drawTechJunction(context);
        break;
      case "brickArchTrim":
        this.drawBricks(context, random, "#18110e", "#684735", "#8a674d", 20, 12);
        break;
      case "darkStoneFloor":
        this.drawStoneFloor(context, random);
        break;
      case "cableTrenchFloor":
        this.drawCableTrench(context, random);
        break;
      case "rivetedBlastDoor":
        this.drawDoor(context, false);
        this.drawRivets(context);
        break;
      case "amberTerminal":
        this.drawComputerPanel(context, random);
        context.fillStyle = "rgba(229,155,54,.32)";
        context.fillRect(8, 8, 110, 112);
        break;
      case "greenEnergyChannel":
        this.drawEnergyChannel(context, random);
        break;
      case "ritualReactorCasing":
        this.drawPanels(context, random, "#181411", "#554232", 32);
        this.drawTechJunction(context);
        break;
      case "organicCorruption":
        this.drawOrganicCorruption(context, random);
        break;
      case "bloodSmear":
        this.drawSplatter(context, random, "#691812");
        break;
      case "ashDecal":
        this.drawSplatter(context, random, "rgba(72,69,65,.72)");
        break;
      case "hazardPlateMuted":
        this.drawWarningStripes(context);
        context.fillStyle = "rgba(34,24,18,.55)";
        context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
        break;
    }
  }

  private drawPanels(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    dark: string,
    light: string,
    panelSize: number,
  ): void {
    context.fillStyle = dark;
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    for (let y = 0; y < TEXTURE_SIZE; y += panelSize) {
      for (let x = 0; x < TEXTURE_SIZE; x += panelSize) {
        const variation = random.int(-10, 12);
        context.fillStyle = shiftHex(light, variation);
        context.fillRect(x + 2, y + 2, panelSize - 4, panelSize - 4);
        context.fillStyle = "rgba(0,0,0,.32)";
        context.fillRect(x + 2, y + panelSize - 5, panelSize - 4, 3);
        context.fillStyle = PALETTE.black;
        context.fillRect(x + 5, y + 5, 2, 2);
        context.fillRect(x + panelSize - 7, y + 5, 2, 2);
      }
    }
    this.drawScratches(context, random, 14, "rgba(10,12,10,.45)");
  }

  private drawBricks(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    mortar: string,
    brickDark: string,
    brickLight: string,
    brickWidth = 32,
    brickHeight = 16,
  ): void {
    context.fillStyle = mortar;
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    for (let y = 0; y < TEXTURE_SIZE; y += brickHeight) {
      const stagger = (Math.floor(y / brickHeight) % 2) * (brickWidth / 2);
      for (let x = -brickWidth; x < TEXTURE_SIZE + brickWidth; x += brickWidth) {
        const brickX = Math.round(x + stagger);
        context.fillStyle = shiftHex(random.chance(0.45) ? brickDark : brickLight, random.int(-14, 9));
        context.fillRect(brickX + 2, y + 2, brickWidth - 4, brickHeight - 4);
        this.drawStoneGrain(context, random, brickX + 3, y + 3, brickWidth - 7, brickHeight - 7);
        context.fillStyle = "rgba(255,230,190,.16)";
        context.fillRect(brickX + 3, y + 3, brickWidth - 7, 2);
        if (random.chance(0.28)) {
          context.fillStyle = "rgba(10,6,4,.5)";
          context.fillRect(brickX + random.int(5, brickWidth - 8), y + random.int(6, brickHeight - 5), random.int(3, 9), 2);
        }
      }
    }
  }

  private drawCeilingTiles(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
  ): void {
    const tileSize = 32;
    context.fillStyle = "#101511";
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    for (let y = 0; y < TEXTURE_SIZE; y += tileSize) {
      for (let x = 0; x < TEXTURE_SIZE; x += tileSize) {
        context.fillStyle = shiftHex(random.chance(0.5) ? "#4a5047" : "#596055", random.int(-9, 8));
        context.fillRect(x + 2, y + 2, tileSize - 4, tileSize - 4);
        context.fillStyle = "rgba(210,220,190,.09)";
        context.fillRect(x + 3, y + 3, tileSize - 6, 2);
        context.fillStyle = "rgba(0,0,0,.34)";
        context.fillRect(x + 3, y + tileSize - 6, tileSize - 6, 3);
        if (random.chance(0.42)) {
          context.strokeStyle = "rgba(15,18,14,.7)";
          context.lineWidth = 1;
          context.beginPath();
          const crackX = x + random.int(8, 24);
          const crackY = y + random.int(5, 14);
          context.moveTo(crackX, crackY);
          context.lineTo(crackX + random.int(-6, 7), crackY + random.int(5, 12));
          context.lineTo(crackX + random.int(-9, 9), crackY + random.int(12, 18));
          context.stroke();
        }
      }
    }
    this.drawScratches(context, random, 10, "rgba(8,10,8,.28)");
  }

  private drawStoneFloor(context: CanvasRenderingContext2D, random: SeededRandom): void {
    context.fillStyle = "#171e19";
    context.fillRect(0, 0, 128, 128);
    for (let y = 0; y < 128; y += 32) {
      for (let x = 0; x < 128; x += 32) {
        context.fillStyle = shiftHex("#596052", random.int(-15, 12));
        context.fillRect(x + 1, y + 1, 30, 30);
        context.fillStyle = "rgba(187,197,163,.22)";
        context.fillRect(x + 2, y + 2, 28, 1);
        context.fillRect(x + 2, y + 2, 1, 27);
        context.fillStyle = "rgba(5,12,8,.45)";
        context.fillRect(x + 2, y + 29, 28, 2);
        this.drawStoneGrain(context, random, x + 2, y + 2, 27, 27);
        if (random.chance(0.55)) {
          context.strokeStyle = "#2b352c";
          context.lineWidth = 0.6;
          context.beginPath();
          context.moveTo(x + 3, y + 11);
          context.lineTo(x + 12, y + 15);
          context.lineTo(x + 17, y + 28);
          context.stroke();
        }
        for (let tuft = 0; tuft < 18; tuft += 1) {
          context.fillStyle = random.pick(["#283d25", "#415734", "#627344"]);
          context.fillRect(x + random.int(0, 31), y + random.int(28, 31), random.int(1, 3), 1);
        }
      }
    }
  }

  private drawStoneGrain(context: CanvasRenderingContext2D, random: SeededRandom,
    x: number, y: number, width: number, height: number): void {
    for (let grain = 0; grain < 100; grain += 1) {
      context.fillStyle = random.chance(0.5) ? "rgba(8,14,9,.18)" : "rgba(212,216,181,.14)";
      context.fillRect(x + random.range(0, width), y + random.range(0, height), 0.5 + random.next(), 0.5);
    }
  }

  private drawVegetationPatches(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    surface: "ceiling" | "wall",
  ): void {
    // Fine moss undergrowth, with distinct stems and paired leaves above it.
    for (let stem = 0; stem < (surface === "wall" ? 6 : 4); stem += 1) {
      let x = random.int(5, 122);
      let y = surface === "wall" ? 126 : random.int(30, 115);
      const length = random.int(9, 19);
      for (let segment = 0; segment < length; segment += 1) {
        const nextX = x + random.int(-3, 3);
        const nextY = y - 4;
        context.strokeStyle = "#202d17";
        context.lineWidth = 1.5;
        context.beginPath();
        context.moveTo(x + 1, y + 1);
        context.lineTo(nextX + 1, nextY + 1);
        context.stroke();
        context.strokeStyle = "#698047";
        context.lineWidth = 0.65;
        context.beginPath();
        context.moveTo(x, y);
        context.lineTo(nextX, nextY);
        context.stroke();
        for (const direction of [-1, 1]) {
          context.fillStyle = random.pick(["#344c29", "#526b36", "#718445", "#879852"]);
          context.beginPath();
          context.ellipse(nextX + direction * 2.3, nextY - 0.5, 3.2, 1.4, direction * 0.6, 0, Math.PI * 2);
          context.fill();
          context.fillStyle = "rgba(179,193,112,.35)";
          context.fillRect(nextX + direction * 2, nextY - 1, 1.5, 0.5);
        }
        x = nextX;
        y = nextY;
        if (y < 4) break;
      }
    }
    const colors = ["#21311c", "#344a27", "#526537", "#70834a"] as const;
    const clusters = surface === "ceiling" ? 10 : 7;
    for (let cluster = 0; cluster < clusters; cluster += 1) {
      const originX = random.int(0, TEXTURE_SIZE - 1);
      const originY = surface === "wall" ? random.int(72, 124) : random.int(0, 127);
      const pieces = random.int(7, 16);
      for (let piece = 0; piece < pieces; piece += 1) {
        const color = colors[random.int(0, colors.length - 1)] as string;
        const spreadX = random.int(-14, 14);
        const spreadY = surface === "wall" ? random.int(-28, 7) : random.int(-12, 12);
        context.fillStyle = color;
        context.fillRect(
          (originX + spreadX + TEXTURE_SIZE) % TEXTURE_SIZE,
          Math.max(0, Math.min(TEXTURE_SIZE - 1, originY + spreadY)),
          random.int(1, 3),
          random.int(1, 2),
        );
      }
      if (surface === "wall" && random.chance(0.72)) {
        context.strokeStyle = colors[random.int(1, colors.length - 1)] as string;
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(originX, originY);
        context.bezierCurveTo(
          originX + random.int(-9, 9),
          originY - 16,
          originX + random.int(-12, 12),
          originY - 34,
          originX + random.int(-8, 8),
          originY - random.int(38, 58),
        );
        context.stroke();
      }
    }
  }

  private drawSoot(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    opacity = 0.34,
  ): void {
    for (let index = 0; index < 45; index += 1) {
      const shade = random.int(4, 20);
      context.fillStyle = `rgba(${shade},${shade},${shade},${opacity})`;
      context.fillRect(random.int(0, 127), random.int(0, 127), random.int(4, 18), random.int(2, 8));
    }
  }

  private drawTechJunction(context: CanvasRenderingContext2D): void {
    context.fillStyle = "#171b19";
    context.fillRect(49, 0, 30, 128);
    context.fillStyle = "#53574e";
    context.fillRect(53, 0, 5, 128);
    context.fillRect(70, 0, 5, 128);
    context.fillStyle = "#d67827";
    for (let y = 10; y < 128; y += 26) context.fillRect(60, y, 8, 5);
  }

  private drawCableTrench(context: CanvasRenderingContext2D, random: SeededRandom): void {
    this.drawFloor(context, random, "#211d19");
    context.fillStyle = "#090a09";
    context.fillRect(26, 0, 76, 128);
    context.fillStyle = "#4d4b44";
    context.fillRect(22, 0, 5, 128);
    context.fillRect(101, 0, 5, 128);
    const cableColors = ["#171b1d", "#68241d", "#343a36", "#8a4f24"];
    cableColors.forEach((color, index) => {
      context.fillStyle = color;
      context.fillRect(32 + index * 16, 0, 8, 128);
    });
    context.fillStyle = "#665c4a";
    for (let y = 8; y < 128; y += 24) context.fillRect(24, y, 80, 4);
  }

  private drawRivets(context: CanvasRenderingContext2D): void {
    context.fillStyle = "#aaa17f";
    for (const x of [12, 116]) {
      for (let y = 10; y < 128; y += 18) context.fillRect(x, y, 3, 3);
    }
  }

  private drawEnergyChannel(context: CanvasRenderingContext2D, random: SeededRandom): void {
    context.fillStyle = "#07150d";
    context.fillRect(0, 0, 128, 128);
    context.fillStyle = "#143c22";
    context.fillRect(14, 0, 100, 128);
    context.fillStyle = "#5bd46f";
    context.fillRect(48, 0, 32, 128);
    context.fillStyle = "#b2ff91";
    for (let y = 0; y < 128; y += 16) context.fillRect(54 + random.int(-3, 3), y, 20, 7);
    context.fillStyle = "#202a23";
    for (let y = 10; y < 128; y += 24) context.fillRect(7, y, 114, 4);
  }

  private drawOrganicCorruption(context: CanvasRenderingContext2D, random: SeededRandom): void {
    context.fillStyle = "#160b0b";
    context.fillRect(0, 0, 128, 128);
    context.strokeStyle = "#6d201b";
    context.lineWidth = 7;
    for (let branch = 0; branch < 12; branch += 1) {
      context.beginPath();
      context.moveTo(64, 64);
      context.bezierCurveTo(
        random.int(20, 108),
        random.int(20, 108),
        random.int(0, 127),
        random.int(0, 127),
        random.int(0, 127),
        random.int(0, 127),
      );
      context.stroke();
    }
    context.fillStyle = "#b0492e";
    for (let index = 0; index < 18; index += 1) {
      context.fillRect(random.int(4, 122), random.int(4, 122), 3, 3);
    }
  }

  private drawScratches(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    count: number,
    color: string,
  ): void {
    context.strokeStyle = color;
    context.lineWidth = 1;
    for (let index = 0; index < count; index += 1) {
      const x = random.int(0, TEXTURE_SIZE - 1);
      const y = random.int(0, TEXTURE_SIZE - 1);
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x + random.int(-12, 12), y + random.int(2, 18));
      context.stroke();
    }
  }

  private drawComputerPanel(context: CanvasRenderingContext2D, random: SeededRandom): void {
    this.drawPanels(context, random, PALETTE.black, PALETTE.iron, 64);
    for (let y = 10; y < 118; y += 28) {
      context.fillStyle = "#081d1b";
      context.fillRect(10, y, 74, 18);
      context.fillStyle = PALETTE.cyan;
      for (let line = 0; line < 4; line += 1) {
        context.fillRect(15 + random.int(0, 8), y + 4 + line * 3, random.int(12, 55), 1);
      }
      context.fillStyle = random.chance(0.4) ? PALETTE.red : PALETTE.amber;
      context.fillRect(96, y + 3, 8, 8);
      context.fillStyle = PALETTE.steel;
      context.fillRect(96, y + 13, 20, 3);
    }
  }

  private drawWarningStripes(context: CanvasRenderingContext2D): void {
    context.fillStyle = PALETTE.black;
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    context.fillStyle = PALETTE.amber;
    for (let x = -TEXTURE_SIZE; x < TEXTURE_SIZE * 2; x += 32) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x + 18, 0);
      context.lineTo(x + TEXTURE_SIZE + 18, TEXTURE_SIZE);
      context.lineTo(x + TEXTURE_SIZE, TEXTURE_SIZE);
      context.closePath();
      context.fill();
    }
  }

  private drawFloor(context: CanvasRenderingContext2D, random: SeededRandom, base: string): void {
    context.fillStyle = base;
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    context.strokeStyle = "#151915";
    context.lineWidth = 3;
    for (let offset = 0; offset <= TEXTURE_SIZE; offset += 32) {
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset, TEXTURE_SIZE);
      context.moveTo(0, offset);
      context.lineTo(TEXTURE_SIZE, offset);
      context.stroke();
    }
    for (let index = 0; index < 80; index += 1) {
      const tone = random.int(30, 75);
      context.fillStyle = `rgba(${tone},${tone + 5},${tone},.28)`;
      context.fillRect(random.int(0, 127), random.int(0, 127), 2, 2);
    }
  }

  private drawToxic(context: CanvasRenderingContext2D, random: SeededRandom): void {
    context.fillStyle = "#172b1b";
    context.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
    for (let index = 0; index < 70; index += 1) {
      const radius = random.int(2, 11);
      context.fillStyle = random.chance(0.5) ? "rgba(106,168,61,.5)" : "rgba(185,232,79,.3)";
      context.beginPath();
      context.arc(random.int(0, 127), random.int(0, 127), radius, 0, Math.PI * 2);
      context.fill();
    }
    context.strokeStyle = "rgba(200,255,100,.28)";
    for (let y = 12; y < 128; y += 24) {
      context.beginPath();
      for (let x = 0; x <= 128; x += 8) {
        context.lineTo(x, y + Math.sin(x * 0.18) * 3);
      }
      context.stroke();
    }
  }

  private drawDoor(context: CanvasRenderingContext2D, exit: boolean): void {
    context.fillStyle = PALETTE.black;
    context.fillRect(0, 0, 128, 128);
    context.fillStyle = exit ? "#394a3d" : PALETTE.iron;
    context.fillRect(8, 2, 112, 126);
    context.fillStyle = PALETTE.steel;
    for (let x = 12; x < 118; x += 18) {
      context.fillRect(x, 6, 8, 116);
    }
    context.fillStyle = exit ? PALETTE.cyan : PALETTE.amber;
    context.fillRect(48, 46, 32, 26);
    context.fillStyle = PALETTE.black;
    context.fillRect(53, 51, 22, 16);
    if (exit) {
      context.fillStyle = PALETTE.cyan;
      context.fillRect(57, 57, 14, 4);
    }
  }

  private drawCrate(context: CanvasRenderingContext2D, random: SeededRandom): void {
    context.fillStyle = PALETTE.darkRust;
    context.fillRect(0, 0, 128, 128);
    context.fillStyle = PALETTE.rust;
    context.fillRect(8, 8, 112, 112);
    context.fillStyle = PALETTE.iron;
    context.fillRect(12, 12, 12, 104);
    context.fillRect(104, 12, 12, 104);
    context.fillRect(12, 54, 104, 18);
    this.drawScratches(context, random, 18, "rgba(20,10,6,.55)");
  }

  private drawSplatter(
    context: CanvasRenderingContext2D,
    random: SeededRandom,
    color: string,
  ): void {
    context.clearRect(0, 0, 128, 128);
    context.fillStyle = color;
    for (let index = 0; index < 24; index += 1) {
      context.beginPath();
      context.arc(
        64 + random.int(-38, 38),
        64 + random.int(-35, 35),
        random.int(2, 13),
        0,
        Math.PI * 2,
      );
      context.fill();
    }
  }

  private drawBurst(context: CanvasRenderingContext2D, color: string): void {
    context.clearRect(0, 0, 128, 128);
    context.translate(64, 64);
    context.fillStyle = color;
    for (let index = 0; index < 16; index += 1) {
      context.rotate(Math.PI / 8);
      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(8, 5);
      context.lineTo(50 - (index % 3) * 8, 0);
      context.lineTo(8, -5);
      context.closePath();
      context.fill();
    }
    context.fillStyle = "#fff3b0";
    context.beginPath();
    context.arc(0, 0, 17, 0, Math.PI * 2);
    context.fill();
    context.setTransform(1, 0, 0, 1, 0, 0);
  }
}

function shiftHex(hex: string, amount: number): string {
  const parsed = Number.parseInt(hex.slice(1), 16);
  const red = Math.max(0, Math.min(255, (parsed >> 16) + amount));
  const green = Math.max(0, Math.min(255, ((parsed >> 8) & 0xff) + amount));
  const blue = Math.max(0, Math.min(255, (parsed & 0xff) + amount));
  return `rgb(${red},${green},${blue})`;
}
